import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CircleCheckBig, CircleX, Clock, Download, Eye, FileSpreadsheet, Lock, RefreshCw, Save, Search, Send, TriangleAlert, Undo2,
} from 'lucide-react';
import * as hr from '../api';
import Loader from '../components/Loader';
import { useHR } from '../HRContext';
import Modal from '../components/Modal';
import { MonthInput, Select } from '../components/Controls';
import { download, fmtMoney, fmtNumber, toCSV } from '../format';
import { Chip, Kpi } from './attendance/shared';

// Time & Attendance > Timesheet (Stephanet's TimesheetRegister): one timesheet per employee per month, built from attendance
// (weekdays, Saturdays, Sundays/holidays, normal and OT hours) and costed. Pending → Submitted (TIMESHEET approval) → Approved
// → Locked; locking puts its day / weekend pay and transport on the payroll inputs register (approved overtime is already there).

const STATUS_TONE = { Locked: 'green', Approved: 'blue', Submitted: 'yellow', Pending: 'red', Rejected: 'red', Returned: 'orange' };
const STATUS_ICON = { Locked: CircleCheckBig, Approved: CircleCheckBig, Submitted: Clock, Pending: TriangleAlert, Rejected: CircleX, Returned: Undo2 };
const amt = v => (Number(v) > 0 ? fmtMoney(v) : '—');
const n = v => (v === null || v === undefined ? '—' : fmtNumber(v));
const nz = v => (Number(v) > 0 ? fmtNumber(v) : '—');
const thisMonth = () => new Date().toISOString().slice(0, 7);
// key: [label, icon, class, statuses it applies to, right]
const ACTIONS = {
  submit: ['Submit', Send, 'b-blue', ['Pending', 'Returned', 'Rejected'], 'create'],
  approve: ['Approve', CircleCheckBig, 'b-green', ['Submitted'], 'approve'],
  reject: ['Reject', CircleX, 'b-red', ['Submitted'], 'approve'],
  return: ['Return', Undo2, 'b-orange', ['Submitted', 'Approved'], 'approve'],
  lock: ['Lock', Lock, 'b-outline', ['Approved'], 'approve'],
};
const COLS = [
  ['Timesheet ID', r => r.reference], ['Emp ID', r => r.employee_no], ['Name', r => r.name], ['Site', r => r.site || ''], ['Type', r => r.type],
  ['Days Worked', r => r.days_worked], ['Absent', r => r.absent], ['WK Days', r => r.wk_days], ['Sat', r => r.sat], ['Sun', r => r.sun],
  ['Normal Hrs', r => r.normal_hrs], ['OT Hrs', r => r.ot_hrs], ['Wk Amt', r => r.wk_amt], ['Sat Cost', r => r.sat_cost], ['Sun Cost', r => r.sun_cost],
  ['OT Pay', r => r.ot_pay], ['Transport', r => r.transport], ['Total', r => r.total], ['Approved By', r => r.approved_by || ''], ['Status', r => r.status],
];
const LEFT = ['Timesheet ID', 'Emp ID', 'Name', 'Site', 'Type', 'Approved By', 'Status'];

export default function TimesheetRegister() {
  const { notify } = useHR();
  const [period, setPeriod] = useState(thisMonth());
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [site, setSite] = useState('all');
  const [status, setStatus] = useState('all');
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [reason, setReason] = useState(null);

  const load = useCallback((p = period) => hr.getTimesheets(p).then(x => { setD(x); setError(''); setSel([]); return x; }).catch(e => setError(e.message)), [period]);
  useEffect(() => { setD(null); load(period); }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    if (!d) return [];
    const s = q.trim().toLowerCase();
    return d.rows.filter(r => (!s || r.name.toLowerCase().includes(s) || String(r.employee_no || '').toLowerCase().includes(s))
      && (site === 'all' || r.site === site) && (status === 'all' || r.status === status));
  }, [d, q, site, status]);

  if (error && !d) return <div className="mdr"><div className="mdr-card mdr-empty">{error}</div></div>;
  if (!d) return <Loader title="Loading timesheets…" subtitle="Fetching the month" />;

  const can = d.can;
  const generate = () => {
    setBusy(true);
    hr.generateTimesheets(period).then(r => { notify(r.message); return load(); })
      .catch(e => notify(e.message, 'bad')).finally(() => setBusy(false));
  };
  const act = (action, ids, comment) => {
    setBusy(true);
    return hr.timesheetAction({ action, ids, comment })
      .then(r => { notify(r.message); setReason(null); return load(); })
      .catch(e => notify(e.message, 'bad')).finally(() => setBusy(false));
  };
  const run = (action, ids) => (['reject', 'return'].includes(action) ? setReason({ action, ids }) : act(action, ids));
  const exportCsv = () => download(`timesheet-register-${d.period}.csv`, toCSV(COLS.map(([label, get]) => ({ label, get })), rows));
  const chosen = d.rows.filter(r => sel.includes(r.id));
  const allOn = rows.length > 0 && rows.every(r => sel.includes(r.id));
  const sum = k => rows.reduce((a, r) => a + Number(r[k] || 0), 0);

  return (
    <div className="mdr mds ts">
      <div className="mdr-top">
        <div>
          <h2>Timesheet Register</h2>
          <p>Multi-site attendance, OT, Sunday/Holiday tracking — {d.label}</p>
        </div>
        <div className="mdr-top-actions">
          <MonthInput className="ts-period" value={period} onChange={e => e.target.value && setPeriod(e.target.value)} />
          {can.create && <button className="mdr-btn" disabled={busy} onClick={generate}><RefreshCw size={14} className={busy ? 'spin' : ''} /> {d.rows.length ? 'Refresh from Attendance' : 'Generate from Attendance'}</button>}
        </div>
      </div>

      <div className="md-kpis mds-kpis ts-kpis">
        <Kpi label="Total Records" value={d.kpis.total} Ico={FileSpreadsheet} tone="blue" note={`${d.label} timesheets`} />
        <Kpi label="Approved/Locked" value={d.kpis.approved} Ico={CircleCheckBig} tone="green" note="Ready for payroll" i={1} />
        <Kpi label="Pending Submission" value={d.kpis.pending} Ico={TriangleAlert} tone="red" note="Pending, returned or rejected" i={2} />
        <Kpi label="Awaiting Approval" value={d.kpis.awaiting} Ico={Clock} tone="orange" note={`Register total ${fmtMoney(d.kpis.amount)}`} i={3} />
      </div>

      <div className="ts-filters no-print">
        <div className="mdr-search"><Search size={14} /><input placeholder="Search employee..." value={q} onChange={e => setQ(e.target.value)} /></div>
        <Select value={site} onChange={e => setSite(e.target.value)}><option value="all">All Sites</option>{d.sites.map(s => <option key={s} value={s}>{s}</option>)}</Select>
        <Select value={status} onChange={e => setStatus(e.target.value)}><option value="all">All Status</option>{d.statuses.map(s => <option key={s} value={s}>{s}</option>)}</Select>
        {can.export && <button className="mdr-btn" onClick={exportCsv} disabled={!rows.length}><Download size={14} /> Export</button>}
      </div>

      {chosen.length > 0 && (
        <div className="mdr-bulk no-print">
          <b>{chosen.length} selected</b>
          {Object.entries(ACTIONS).map(([k, [label, Ico, cls, from, right]]) => {
            const ok = chosen.filter(r => from.includes(r.status)).length;
            return can[right] && <button key={k} className={`mdr-bulk-btn ${cls}`} disabled={busy || !ok} onClick={() => run(k, chosen.filter(r => from.includes(r.status)).map(r => r.id))}><Ico size={13} /> {label}{ok ? ` (${ok})` : ''}</button>;
          })}
          <button className="mdr-bulk-btn b-outline" onClick={() => setSel([])}>Clear</button>
        </div>
      )}

      <div className="mdr-card mdr-table-card">
        <div className="mds-table-head"><b>Timesheet Register</b><span className="mds-note-inline">{rows.length} records</span></div>
        <div className="mdr-scroll">
          <table className="mdr-table mds-table ts-table">
            <thead>
              <tr>
                <th className="c no-print"><input type="checkbox" checked={allOn} onChange={() => setSel(allOn ? [] : rows.map(r => r.id))} aria-label="Select all" /></th>
                <th className="c no-print">Action</th>
                {COLS.map(([h]) => <th key={h} className={LEFT.includes(h) ? '' : 'c'}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const Ico = STATUS_ICON[r.status] || Clock;
                return (
                  <tr key={r.id} className={`mdr-row ${r.status === 'Pending' ? 'ts-pending' : ''}`} style={{ animationDelay: `${Math.min(i, 20) * 15}ms` }}>
                    <td className="c no-print"><input type="checkbox" checked={sel.includes(r.id)} onChange={() => setSel(s => (s.includes(r.id) ? s.filter(x => x !== r.id) : [...s, r.id]))} aria-label={`Select ${r.reference}`} /></td>
                    <td className="c no-print"><button className="mds-view" onClick={() => setViewing(r.id)}><Eye size={12} /> View</button></td>
                    <td className="mono">{r.reference}</td>
                    <td className="mono ts-emp">{r.employee_no}</td>
                    <td className="nowrap"><b>{r.name}</b></td>
                    <td className="small">{r.site || '—'}</td>
                    <td><span className={`ts-type ${r.type === 'BASIC' ? 'basic' : 'daily'}`}>{r.type}</span></td>
                    <td className="c b">{n(r.days_worked)}</td><td className={`c ${r.absent ? 'rd' : ''}`}>{n(r.absent)}</td><td className="c">{n(r.wk_days)}</td>
                    <td className="c">{n(r.sat)}</td><td className="c">{n(r.sun)}</td><td className="c">{n(r.normal_hrs)}</td>
                    <td className="c ts-ot">{nz(r.ot_hrs)}</td>
                    <td className="r">{fmtMoney(r.wk_amt)}</td><td className="r">{amt(r.sat_cost)}</td><td className="r">{amt(r.sun_cost)}</td>
                    <td className="r ts-ot">{amt(r.ot_pay)}</td><td className="r">{amt(r.transport)}{r.transport_manual && <sup title="Set manually">✎</sup>}</td>
                    <td className="r b">{fmtMoney(r.total)}</td>
                    <td className="small">{r.approved_by || '—'}</td>
                    <td><span className={`mdr-chip t-${STATUS_TONE[r.status]} ts-status`}><Ico size={11} /> {r.status}</span></td>
                  </tr>
                );
              })}
              {!rows.length && (
                <tr><td colSpan={COLS.length + 2} className="mdr-empty">
                  {d.rows.length ? 'No timesheets match the filters.'
                    : d.has_attendance ? <>No timesheets for {d.label} yet. {can.create && <button className="mds-run sm" onClick={generate} disabled={busy}>Generate from Attendance</button>}</>
                      : `No attendance recorded for ${d.label}.`}
                </td></tr>
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr>
                  <td className="no-print" /><td className="no-print" /><td colSpan={5}><b>Total — {rows.length} timesheet(s)</b></td>
                  {['days_worked', 'absent', 'wk_days', 'sat', 'sun', 'normal_hrs', 'ot_hrs'].map(k => <td key={k} className="c">{fmtNumber(sum(k))}</td>)}
                  {['wk_amt', 'sat_cost', 'sun_cost', 'ot_pay', 'transport', 'total'].map(k => <td key={k} className="r">{fmtMoney(sum(k))}</td>)}
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="mdr-foot att2-foot"><span>Sun includes public holidays worked · Total = Wk Amt + Sat Cost + Sun Cost + OT Pay + Transport · Rates follow the attendance policy and OT rate table · OT counts approved Overtime Authorizations when overtime must be authorised · Locking posts day/weekend pay and transport to payroll inputs</span></div>
      </div>

      {viewing && <TimesheetView id={viewing} busy={busy} onClose={() => setViewing(null)} onAction={(a, id) => run(a, [id])} onSaved={load} notify={notify} />}
      {reason && <ReasonModal action={reason.action} count={reason.ids.length} busy={busy} onClose={() => setReason(null)} onSubmit={c => act(reason.action, reason.ids, c).then(() => setViewing(null))} />}
    </div>
  );
}

function TimesheetView({ id, busy, onClose, onAction, onSaved, notify }) {
  const [t, setT] = useState(null);
  const [transport, setTransport] = useState('');
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => hr.getTimesheet(id).then(x => { setT(x); setTransport(x.transport ?? ''); setRemarks(x.remarks || ''); }).catch(e => notify(e.message, 'bad')), [id, notify]);
  useEffect(() => { load(); }, [load, busy]);
  if (!t) return <Modal wide title="Timesheet" onClose={onClose}><Loader title="Loading…" subtitle="Fetching the record" /></Modal>;
  const open = ['Pending', 'Returned', 'Rejected'].includes(t.status);
  const b = t.basis || {};
  const save = reset => {
    setSaving(true);
    hr.updateTimesheet(id, { transport: reset || transport === '' ? null : Number(transport), remarks })
      .then(r => { notify(r.message); return Promise.all([load(), onSaved()]); }).catch(e => notify(e.message, 'bad')).finally(() => setSaving(false));
  };
  const figures = [['Wk Amt', t.wk_amt, t.type === 'BASIC' ? 'Monthly basic salary' : `${n(t.wk_days)} weekday(s) × ${fmtMoney(t.daily_rate)}`],
    ['Sat Cost', t.sat_cost, `${n(t.sat)} Saturday(s)${t.sat_cost ? ` × ${fmtMoney(t.daily_rate)} × ${b.saturday_rate}` : t.sat ? ' — normal work day / not payable' : ''}`],
    ['Sun Cost', t.sun_cost, `${n(t.sun)} Sunday/holiday(s)${t.sun_cost ? ` × ${fmtMoney(t.daily_rate)} × ${b.sunday_rate}` : ''}`],
    ['OT Pay', t.ot_pay, `${n(t.ot_hrs)} hr(s) × ${fmtMoney(b.hourly)}/hr × ${b.overtime_rate ? `${b.overtime_rate} (employee rate)` : 'OT rate by type'}${b.ot_source === 'authorized' ? ' · approved overtime only (Overtime Authorization)' : ''}`],
    ['Transport', t.transport, t.transport_manual ? `Set manually (auto ${fmtMoney(t.auto_transport)})` : 'Outstation allowance per day on outstation sites'],
    ['Total', t.total, 'Wk + Sat + Sun + OT + Transport'],
    ['To Payroll on Lock', t.payroll_amount, 'Day / weekend pay and transport — overtime and the monthly basic are paid through their own lines']];
  const can = t.can;
  return (
    <Modal wide className="ts-modal" title={`${t.reference} — ${t.name}`} onClose={onClose}>
      <div className="ts-view">
        <div className="ts-view-head">
          <div>
            <p>{[t.employee_no, t.department, t.site, t.label].filter(Boolean).join(' · ')}</p>
            <p className="muted small">Daily rate {fmtMoney(t.daily_rate)} · Hourly {fmtMoney(b.hourly)} · Work days {b.work_days} · {b.overtime_eligible ? 'Overtime eligible' : 'Not overtime eligible'}</p>
          </div>
          <div className="ts-view-tags"><span className={`ts-type ${t.type === 'BASIC' ? 'basic' : 'daily'}`}>{t.type}</span><Chip tone={STATUS_TONE[t.status]}>{t.status}</Chip></div>
        </div>
        <div className="ts-counts">
          {[['Scheduled', t.scheduled_days], ['Days Worked', t.days_worked], ['Absent', t.absent], ['Leave', t.leave], ['WK Days', t.wk_days], ['Sat', t.sat], ['Sun/Hol', t.sun], ['Normal Hrs', t.normal_hrs], ['OT Hrs', t.ot_hrs]]
            .map(([l, v]) => <div key={l}><b>{n(v)}</b><span>{l}</span></div>)}
        </div>
        <table className="ts-calc"><tbody>{figures.map(([l, v, how]) => <tr key={l} className={l === 'Total' ? 'tot' : ''}><th>{l}</th><td>{fmtMoney(v)}</td><td className="muted">{how}</td></tr>)}</tbody></table>

        {open && can.edit && (
          <div className="ts-edit">
            <label>Transport (₦)<input type="number" min="0" step="any" value={transport} onChange={e => setTransport(e.target.value)} /></label>
            <label className="grow">Remarks<input value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Note for the approver…" /></label>
            <button className="mds-run sm" disabled={saving} onClick={() => save(false)}><Save size={13} /> Save</button>
            {t.transport_manual && <button className="mdr-btn" disabled={saving} onClick={() => save(true)}>Use automatic</button>}
          </div>
        )}
        {!open && t.remarks && <p className="ts-remarks"><b>Remarks:</b> {t.remarks}</p>}

        <h4 className="wh-sub">Daily Breakdown</h4>
        <div className="mdr-scroll ts-days">
          <table className="mdr-table mds-table">
            <thead><tr><th>Date</th><th>Day</th><th>Site</th><th>Category</th><th className="c">Hours</th><th className="c">OT Hrs</th><th className="r">Day Pay</th><th className="r">OT Pay</th><th className="r">Transport</th></tr></thead>
            <tbody>
              {t.days.map(x => (
                <tr key={x.date} className={`mdr-row ts-cat-${(x.category || '').toLowerCase()}`}>
                  <td className="nowrap">{x.date}</td><td>{x.day}</td><td className="small">{x.site || '—'}</td>
                  <td>{x.category}{x.holiday && <small className="muted"> · {x.holiday}</small>}{x.ot_refs?.length > 0 && <small className="muted"> · {x.ot_refs.join(', ')}</small>}</td>
                  <td className="c">{x.hours ? n(x.hours) : '—'}</td>
                  <td className="c ts-ot">{nz(x.ot)}{x.ot_unauthorised > 0 && <small className="ts-unauth" title="Overtime on attendance without an approved Overtime Authorization — not paid">+{n(x.ot_unauthorised)}h not authorised</small>}</td>
                  <td className="r">{amt(x.pay)}</td><td className="r">{amt(x.ot_pay)}</td><td className="r">{amt(x.transport)}</td>
                </tr>
              ))}
              {!t.days.length && <tr><td colSpan={9} className="mdr-empty">No attendance in this period.</td></tr>}
            </tbody>
          </table>
        </div>

        {t.history.length > 0 && (
          <>
            <h4 className="wh-sub">History</h4>
            <ul className="ts-hist">{t.history.map((h, i) => <li key={i}><b>{h.action}</b> · {h.user || '—'} · {(h.at || '').replace('T', ' ').slice(0, 16)}<br /><small>{h.summary}</small></li>)}</ul>
          </>
        )}
        <div className="mdh-form-foot">
          {Object.entries(ACTIONS).filter(([, [, , , from, right]]) => from.includes(t.status) && can[right]).map(([k, [label, Ico, cls]]) => (
            <button key={k} className={`mdr-bulk-btn ${cls}`} disabled={busy} onClick={() => onAction(k, t.id)}><Ico size={13} /> {label}</button>
          ))}
          <span className="wh-spacer" />
          <button className="mds-run" onClick={onClose}>Close</button>
        </div>
      </div>
    </Modal>
  );
}

function ReasonModal({ action, count, busy, onClose, onSubmit }) {
  const [c, setC] = useState('');
  return (
    <Modal title={`${action === 'reject' ? 'Reject' : 'Return'} ${count} timesheet(s)`} onClose={onClose}>
      <form className="mdr-form" onSubmit={e => { e.preventDefault(); if (c.trim()) onSubmit(c.trim()); }}>
        <label className="mdh-field">Reason<textarea rows={3} autoFocus value={c} onChange={e => setC(e.target.value)} placeholder="Why is it being sent back?" /></label>
        <div className="mdh-form-foot">
          <button type="button" className="mdr-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="mds-run" disabled={busy || !c.trim()}>{action === 'reject' ? 'Reject' : 'Return'}</button>
        </div>
      </form>
    </Modal>
  );
}
